import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from './NotificationContext';
import { Bell, X, CheckCheck } from 'lucide-react';
import { emitter } from './UtilityFunctions';

//Map a notification to what clicking it should open: which record and which
//inner modal tab. SubNav resolves the record across its loaded sheet data and
//opens the modal. Sale-related records are keyed by DealAgreementNo, vehicles
//by VehicleID; tab indices match the modal form tabs (4=Other Expenses,
//5=Comments, 6=Sale Details, 7=Payments, 1=Car Details).
export function notificationTarget(n) {
  const m = n.MetaData || {};
  const dealNo = m.DealAgreementNo != null ? m.DealAgreementNo
    : (n.RelatedRecordType === 'Sale' ? n.RelatedRecordID : null);
  const vehId = m.VehicleID != null ? m.VehicleID
    : (n.RelatedRecordType === 'Vehicle' ? n.RelatedRecordID : null);

  switch (n.NotificationType) {
    case 'VehicleAdded':      return vehId  ? { vehicleId: vehId, sheetHint: 'Inventory', tab: 1 } : null;
    case 'VehicleCommitted':  return dealNo ? { dealAgreementNo: dealNo, sheetHint: 'Committed', tab: 6 } : null;
    case 'VehicleSold':       return dealNo ? { dealAgreementNo: dealNo, sheetHint: m.CashCredit === 'Cash' ? 'Cash' : 'Credit', tab: 6 } : null;
    case 'PaymentReceived':   return dealNo ? { dealAgreementNo: dealNo, tab: 7 } : null;
    case 'CommentAdded':      return vehId  ? { vehicleId: vehId, sheetHint: m.RelatedRecordType, tab: 5 } : null;
    case 'ExpenseAdded':      return vehId  ? { vehicleId: vehId, tab: 4 } : null;
    case 'DealNeedsCostReview': return dealNo ? { dealAgreementNo: dealNo, sheetHint: m.Sheet || 'Committed', tab: 6 } : null;
    case 'CreditCleared':
    case 'CreditWriteOff':    return dealNo ? { dealAgreementNo: dealNo, sheetHint: 'CreditCleared', tab: 6 } : null;
    case 'CreditOverdue':     return dealNo ? { dealAgreementNo: dealNo, sheetHint: 'Credit', tab: 7 } : null;
    case 'CommitmentOverdue': return dealNo ? { dealAgreementNo: dealNo, sheetHint: 'Committed', tab: 7 } : null;
    // Opens the car on its Enquiries tab. Returning null when there is no
    // vehicle is right rather than lazy: the general Contact page produces
    // enquiries with no car attached, and there is no record to open for them —
    // they are reached through the Enquiries inbox instead.
    case 'VehicleEnquiry':    return vehId  ? { vehicleId: vehId, sheetHint: 'Inventory', tab: 10 } : null;
    default:                  return null; // e.g. SaleCancelled — vehicle no longer on a sheet
  }
}

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleNotificationClick = (notification) => {
    if (!notification.IsRead) {
      markAsRead(notification.NotificationID);
    }
    //Navigate to the related record's sheet + open its modal on the right tab.
    const target = notificationTarget(notification);
    if (target) {
      emitter.emit('open-notification', target);
      setIsOpen(false);
    }
  };

  const getPriorityStyles = (priority) => {
    switch (priority) {
      case 'High': return 'text-red-500';
      case 'Medium': return 'text-yellow-500';
      case 'Low': return 'text-blue-500';
      default: return 'text-gray-400';
    }
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'High': return '⬆';
      case 'Low': return '⬇';
      default: return '•';
    }
  };

  const getTimeAgo = (timestamp) => {
    const now = new Date();
    const notifTime = new Date(timestamp);
    const diffMs = now - notifTime;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifTime.toLocaleDateString();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 bg-white rounded-full hover:bg-gray-50 transition-all hover:scale-105 active:scale-95 focus:outline-none focus:ring-2 focus:ring-[color:var(--theme-ring)] shadow-sm"
        aria-label="Notifications"
      >
        <Bell className="w-5 h-5 text-gray-700" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 inline-flex items-center justify-center min-w-5 h-5 px-1.5 text-xs font-bold rounded-full border-2 border-gray-100 shadow-lg animate-pulse" style={{ background: 'var(--theme-danger)', color: 'var(--theme-danger-fg)' }}>
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="notif-panel absolute right-0 mt-3 w-96 max-w-[calc(100vw-16px)] bg-white rounded-xl shadow-2xl border border-gray-200 z-[100] max-h-[min(600px,80dvh)] flex flex-col animate-slideDown">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100 rounded-t-xl">
            <h3 className="text-lg font-semibold text-gray-800">
              Notifications
              {unreadCount > 0 && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  ({unreadCount} unread)
                </span>
              )}
            </h3>
            <div className="flex gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    markAllAsRead();
                  }}
                  className="p-1 text-gray-500 hover:text-[var(--theme-accent)] transition-colors rounded hover:bg-gray-100"
                  title="Mark all as read"
                >
                  <CheckCheck className="w-5 h-5" />
                </button>
              )}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setIsOpen(false);
                }}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors rounded hover:bg-gray-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto flex-1 custom-scrollbar">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-gray-400">
                <Bell className="w-12 h-12 mb-3 opacity-50" />
                <p className="text-sm">No notifications yet</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-100">
                {notifications.map((notification) => (
                  <div
                    key={notification.NotificationID}
                    onClick={() => handleNotificationClick(notification)}
                    className={`px-5 py-4 cursor-pointer transition-colors ${
                      notification.IsRead
                        ? 'bg-white hover:bg-gray-50'
                        : 'bg-[var(--theme-accent-tint)] hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {/* Priority Indicator */}
                      <div className={`text-lg mt-0.5 ${getPriorityStyles(notification.Priority)}`}>
                        {getPriorityIcon(notification.Priority)}
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm leading-relaxed ${
                          notification.IsRead ? 'text-gray-700' : 'text-gray-900 font-medium'
                        }`}>
                          {notification.Message}
                        </p>
                        {/* THE MORNING DIGEST IS THE ONE NOTIFICATION WITH A BODY,
                            and this list is the only place it can be read.

                            `Message` holds a headline built from the briefing's
                            first line — it is a varchar(500) and the briefing is
                            4-5 lines, which is why every DigestReady insert used
                            to fail outright (node a889262). The briefing itself
                            travels in MetaData.digest.

                            There IS a NotificationDetailDialog with a per-type
                            switch, and it would be the natural home for this. It
                            is dead code: nothing in the app imports it, so it has
                            never rendered. Putting the digest only there would
                            have meant moving the body out of Message and into a
                            component nobody can reach.

                            `whitespace-pre-line` because the model writes the
                            briefing as separate lines, and a plain element
                            collapses them into one paragraph. */}
                        {notification.NotificationType === 'DigestReady'
                          && notification.MetaData?.digest && (
                          <p className="mt-1 whitespace-pre-line text-xs leading-relaxed text-gray-600">
                            {notification.MetaData.digest}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs">
                          <span className="text-gray-600 font-medium">
                            {notification.FirstName} {notification.LastName}
                          </span>
                          <span className="text-gray-300">•</span>
                          <span className="text-gray-400">
                            {getTimeAgo(notification.CreatedAt)}
                          </span>
                        </div>
                      </div>

                      {/* Unread Indicator */}
                      {!notification.IsRead && (
                        <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0 bg-[var(--theme-accent)]" />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="border-t border-gray-200 px-5 py-3 text-center bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  setIsOpen(false);
                  // Navigate to full notifications page if you have one
                }}
                className="text-sm text-[var(--theme-accent)] hover:text-[var(--theme-accent-hover)] font-semibold transition-colors hover:underline"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}

      {/* Custom Scrollbar Styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: #f3f4f6;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #d1d5db;
          border-radius: 3px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #9ca3af;
        }
      `}</style>
    </div>
  );
};

export default NotificationBell;